class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // "Complete your look" (snippets/leza-cart-upsell.liquid): one listener for every re-render.
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-leza-upsell-add]');
      if (button) this.addUpsell(button);
    });
  }

  // Adds a "Complete your look" product (its chosen variant) and re-renders the drawer from the add
  // response, like product-form.js: one request, the offer box and the list update with it.
  async addUpsell(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const card = button.closest('[data-leza-upsell]');
    const select = card?.querySelector('[data-leza-upsell-variant]');
    const error = card?.querySelector('.leza-cart-upsell__error');
    const id = Number(select ? select.value : button.dataset.variantId);
    if (!id) return;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    if (error) error.hidden = true;
    this.setActiveElement(button);
    try {
      const response = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [{ id, quantity: 1 }],
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (state.status) throw new Error(state.description || state.message || window.cartStrings?.error);
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'leza-upsell', productVariantId: id, cartData: state });
      }
      this.renderContents(state);
    } catch (e) {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
      if (error) {
        error.textContent = e.message || window.cartStrings?.error || 'Error';
        error.hidden = false;
      }
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    // Opened optimistically for an add: the view event waits for the updated cart (renderContents).
    if (!this.pendingRow) this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  // Optimistic add (product-form.js): open now with the product shown as "Adding to cart…" above the
  // current lines. renderContents swaps in the server's drawer (dropping this row); cancelPending undoes it.
  showPending(item, triggeredBy) {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;
    this.pendingRow?.remove();
    this.pendingWasEmpty = this.classList.contains('is-empty');
    this.classList.remove('is-empty');
    this.classList.add('is-adding');

    const row = document.createElement('div');
    row.className = 'leza-cart-pending';
    row.setAttribute('role', 'status');
    if (item.image) {
      const img = document.createElement('img');
      img.className = 'leza-cart-pending__image';
      img.src = item.image;
      img.alt = '';
      img.width = 80;
      img.height = 80;
      row.append(img);
    }
    const text = document.createElement('div');
    text.className = 'leza-cart-pending__text';
    for (const [cls, value] of [
      ['leza-cart-pending__title', item.title],
      ['leza-cart-pending__price', item.price],
      ['leza-cart-pending__note', window.cartStrings?.adding || 'Adding to cart…'],
    ]) {
      if (!value) continue;
      const p = document.createElement('p');
      p.className = cls;
      p.textContent = value;
      text.append(p);
    }
    row.append(text);
    const bar = document.createElement('span');
    bar.className = 'leza-cart-pending__bar';
    bar.setAttribute('aria-hidden', 'true');
    row.append(bar);

    const header = inner.querySelector('.drawer__header');
    if (header) header.after(row);
    else inner.prepend(row);
    this.pendingRow = row;
    this.open(triggeredBy);
  }

  cancelPending() {
    if (!this.pendingRow) return;
    this.pendingRow.remove();
    this.pendingRow = null;
    this.classList.remove('is-adding');
    this.close();
    // After product-form's own clean-up (it drops is-empty in its finally step).
    if (this.pendingWasEmpty) setTimeout(() => this.classList.add('is-empty'));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const wasPending = Boolean(this.pendingRow);
    this.pendingRow = null;
    this.classList.remove('is-adding');
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (this.classList.contains('active')) {
        // Already open (optimistic add, or an add from inside the drawer): keep focus inside the new
        // contents; after an optimistic open the cart view event is sent now, with the updated cart.
        trapFocus(document.getElementById('CartDrawer'), this.querySelector('.drawer__inner') || this.querySelector('.drawer__close'));
        if (wasPending) this.querySelector('cart-drawer-items')?.dispatchViewEvent();
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
